import * as React from 'react'
import styled, { ThemeProvider } from 'styled-components'

import { screenSizes } from '@src/styles/theme'

import { adminMidnightTheme } from '../theme/admin-midnight-theme'
import { useOrgGatewayRpc } from '../hooks/useOrgGatewayRpc'
import type { OrgGatewayResolver } from '../types/admin'
import { AdminGatewayContext } from '../context/AdminGatewayContext'
import { AdminNav } from './AdminNav'
import { OfflineBanner } from './OfflineBanner'
import { PendingApprovalsBadge } from './PendingApprovalsBadge'

export type AdminShellProps = {
  /** The active organization id (null while bootstrapping or on the guest surface). */
  organizationId: string | null
  /** Maps the active org to a gateway endpoint. See spike-ws-org-binding.md. */
  resolver: OrgGatewayResolver
  /** Page body rendered inside the shell (typically a `<Switch>` of admin routes). */
  children: React.ReactNode
}

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  min-width: 360px;

  @media (max-width: ${screenSizes.XS}px) {
    padding: 12px;
  }
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-family: 'NunitoSansSemiBold';
  color: ${(p) => p.theme.white};
`

const EmptyState = styled.div`
  background-color: ${(p) => p.theme.modalBack};
  border: 1px dashed ${(p) => p.theme.input.default};
  border-radius: ${(p) => p.theme.radius};
  padding: 32px 24px;
  color: ${(p) => p.theme.white};
  text-align: center;
  line-height: 1.5;

  & > h3 {
    margin: 0 0 8px;
    font-size: 16px;
    font-family: 'NunitoSansSemiBold';
    color: ${(p) => p.theme.accent};
  }
`

const Outlet = styled.section`
  background-color: ${(p) => p.theme.modalBack};
  border-radius: ${(p) => p.theme.radius};
  padding: 16px;
`

/**
 * AdminShell — FR-001 / FR-017 / FR-021 / FR-022 container for every `/ai/*`
 * admin page.
 *
 * - Binds a `GatewayRpcClient` to the active organization via `useOrgGatewayRpc`.
 * - Renders the persistent `AdminNav` with a badge slot next to the Approvals link.
 * - Renders an `OfflineBanner` when the gateway is unreachable.
 * - Renders a "missing gateway config" empty state when the resolver returns null.
 */
export function AdminShell({ organizationId, resolver, children }: AdminShellProps) {
  const gateway = useOrgGatewayRpc({ organizationId, resolver })
  const { status, missingConfig, lastError, call } = gateway

  const badge = React.useCallback(
    (to: string): React.ReactNode => {
      if (!to.endsWith('/approvals')) return null
      return (
        <PendingApprovalsBadge
          call={call}
          organizationId={organizationId ?? null}
          enabled={status === 'connected'}
          asLink={false}
        />
      )
    },
    [call, organizationId, status],
  )

  return (
    <ThemeProvider theme={adminMidnightTheme}>
      <AdminGatewayContext.Provider value={gateway}>
        <Shell>
          <Header>
            <Title>Equabot admin</Title>
          </Header>

          <AdminNav renderBadge={badge} />

          {status !== 'connected' && !missingConfig ? (
            <OfflineBanner status={status} errorMessage={lastError} />
          ) : null}

          {missingConfig ? (
            <EmptyState>
              <h3>No Equabot gateway configured</h3>
              <p>
                The active organization has no Equabot gateway configuration. Ask your
                operator to set one up before using live RPC-backed admin actions.
              </p>
            </EmptyState>
          ) : null}

          <Outlet>{children}</Outlet>
        </Shell>
      </AdminGatewayContext.Provider>
    </ThemeProvider>
  )
}
